import type {
  ChannelThreadId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { useMemo } from "react";
import { useIntegrations } from "../../hooks/use-integrations";
import {
  useChannelThreads,
  useThreadMessages,
} from "../../hooks/use-support-ticket-data";
import { BLOCK_RULES_BY_CHANNEL } from "./registry";
import type { BlockContext, BlockedResult } from "./types";

interface UseMessageBlockArgs {
  channel: MessageChannel;
  supportTicketId: SupportTicketId;
  threadId: ChannelThreadId;
}

/**
 * Runs the per-channel block rule pipeline for the composer and returns the
 * first matching block, or `null` when sending is allowed.
 */
export function useMessageBlock({
  channel,
  supportTicketId,
  threadId,
}: UseMessageBlockArgs): BlockedResult | null {
  const { data: threads } = useChannelThreads(supportTicketId);
  const { data: messages, isLoading: messagesLoading } = useThreadMessages(
    supportTicketId,
    threadId,
  );
  const { data: integrations } = useIntegrations();

  const thread = useMemo(
    () => threads.find((t) => t.id === threadId),
    [threads, threadId],
  );

  return useMemo(() => {
    const rules = BLOCK_RULES_BY_CHANNEL[channel];
    if (!rules || rules.length === 0) return null;

    const ctx: BlockContext = {
      channel,
      supportTicketId,
      threadId,
      thread,
      messages,
      messagesLoading,
      integrations,
    };

    for (const rule of rules) {
      const blocked = rule(ctx);
      if (blocked !== null) return blocked;
    }
    return null;
  }, [
    channel,
    supportTicketId,
    threadId,
    thread,
    messages,
    messagesLoading,
    integrations,
  ]);
}
